import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { postCommand, getCommandResponse } from "../../api/commandAPI";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  CardContent,
  Typography,
  Stack,
  Card,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  IconButton,
  Menu,
  MenuItem as MenuItemAction,
  ListItemIcon,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  useTheme,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import {getDevices} from "../../api/deviceAPI";

import { ConvertOIDValueToString } from "../../utils/configConverter";
import { ConvertStringValueToOID } from "../../utils/configConverter";
import {
  getNodeConfigurationsByNodeId,
  createNodeConfiguration,
  updateNodeConfiguration,
  deleteNodeConfiguration,
} from "../../api/nodeConfigurationAPI";
import { getPlaceIdFromPathname } from "../../utils/pathUtils";
import { useObjectData, useEnumData } from "../../hooks/useLocalStorageData";
import { useSnackbar } from "../../hooks/useSnackbar";
import { getCurrentTenant } from "../../config/tenantConfig";

export default function Configuration() {
  // Use custom hooks to get data from localStorage
  const objectData = useObjectData();
  const enumData = useEnumData();

  // --- SEND CONFIGURATIONS BUTTON STATE ---
  const [isCommandRunning, setIsCommandRunning] = useState(false);
  const [isLoadingSendConfig, setIsLoadingSendConfig] = useState(false);
  const [cmdResponseSendConfig, setCmdResponseSendConfig] = useState(null);
  const pollIntervalRefSendConfig = useRef(null);

  // --- SEND CONFIGURATIONS BUTTON HANDLER ---
  const handleSendConfigurations = async () => {
    if (isCommandRunning) return;
    setIsCommandRunning(true);
    if (!selectedDevice) {
      setCmdResponseSendConfig("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    setIsLoadingSendConfig(true);
    setCmdResponseSendConfig(null);
    const cmdKey = uuidv4();
    const CmdStr = "getConfig";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, []);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRefSendConfig.current) {
        clearInterval(pollIntervalRefSendConfig.current);
        pollIntervalRefSendConfig.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++; 
        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );
          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRefSendConfig.current = null;
            setCmdResponseSendConfig(response[0].Value);
            setIsLoadingSendConfig(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRefSendConfig.current = null;
            setCmdResponseSendConfig("No response received after 30 seconds");
            setIsLoadingSendConfig(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRefSendConfig.current = null;
            setCmdResponseSendConfig(
              "Failed to get response after multiple attempts"
            );
            setIsLoadingSendConfig(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);
      pollIntervalRefSendConfig.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoadingSendConfig(false);
      setIsCommandRunning(false);
    }
  };

  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const rawId = getPlaceIdFromPathname(location.pathname);
  const node_id = rawId ? parseInt(rawId, 10) : null;
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(0);
  const [devices, setDevices] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    deviceType: "",
    value: "",
    object_idObject: "",
  });
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    configId: null,
  });
  const [isSubmittingConfig, setIsSubmittingConfig] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Track editing via editingConfig only

  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleUpdate = () => {
    const configToEdit = allRows.find(
      (row) => row.idNodeConfiguration === selectedRowId
    );
    if (configToEdit) {
      setFormData({
        deviceType: configToEdit.deviceType, // Bu row'daki cihaz tipi kullanılacak
        value: configToEdit.nodeValue || configToEdit.defaultValue,
        object_idObject: configToEdit.object_idObject,
      });
      setEditingConfig(configToEdit);
      setDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleAddConfigurationToNode = () => {
    const configToAdd = allRows.find(
      (row) =>
        row.idNodeConfiguration === selectedRowId ||
        `${row.object_idObject}-${row.deviceType}-${
          row.node_idNode || "default"
        }` === selectedRowId
    );
    if (configToAdd) {
      setFormData({
        deviceType: configToAdd.deviceType,
        value: configToAdd.defaultValue || "",
        object_idObject: configToAdd.object_idObject,
      });
      setEditingConfig(null);
      setDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteConfirmDialog({
      open: true,
      configId: selectedRowId,
    });
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      if (isDeleting) return;
      setIsDeleting(true);

      await deleteNodeConfiguration(deleteConfirmDialog.configId);

      showSnackbar(
        t("nodeComponents.configuration.deleteSuccess") ||
          "Configuration deleted successfully",
        "success"
      );

      await refreshConfigurations();
    } catch (error) {
      console.error("Error deleting configuration:", error);
      showSnackbar(
        error.message ||
          t("nodeComponents.configuration.deleteError") ||
          "Failed to delete configuration",
        "error"
      );
    } finally {
      setDeleteConfirmDialog({ open: false, configId: null });
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmDialog({ open: false, configId: null });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingConfig(null);
    setFormData({
      deviceType: "",
      value: "",
      object_idObject: "",
    });
  };

  const handleFormChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
      ...(field === "object_idObject" && { value: "" }),
    });
  };

  const handleSubmit = async () => {
    try {
      if (isSubmittingConfig) return; // guard against double clicks
      setIsSubmittingConfig(true);
      const adminId = localStorage.getItem("adminId");
      const selectedObject = objectData.find(
        (obj) => obj.idObject === parseInt(formData.object_idObject, 10)
      );

      let valueToPost = formData.value;
      if (selectedObject) {
        try {
          valueToPost = ConvertStringValueToOID(selectedObject, formData.value);
        } catch (error) {
          console.warn("Error converting value, using original:", error);
          valueToPost = formData.value;
        }
      }

      const configurationData = {
        idNodeConfiguration: editingConfig
          ? editingConfig.idNodeConfiguration
          : 0,
        deviceType: parseInt(formData.deviceType, 10),
        value: valueToPost,
        object_idObject: parseInt(formData.object_idObject, 10),
        node_idNode: node_id,
        admin_idAdmin: parseInt(adminId, 10),
      };

      if (editingConfig) {
        await updateNodeConfiguration(configurationData);
      } else {
        await createNodeConfiguration(configurationData);
      }

      showSnackbar(
        t(
          `nodeComponents.configuration.${
            editingConfig ? "updateSuccess" : "addSuccess"
          }`
        ) || "Configuration saved successfully",
        "success"
      );

      handleDialogClose();

      await refreshConfigurations();
    } catch (error) {
      console.error("Error saving configuration:", error);
      showSnackbar(
        error.message ||
          t(
            `nodeComponents.configuration.${
              editingConfig ? "updateError" : "addError"
            }`
          ) ||
          "Failed to save configuration",
        "error"
      );
    } finally {
      setIsSubmittingConfig(false);
    }
  };

  const columns = [
    {
      field: "objectName",
      headerName: t("nodeComponents.configuration.objectName") || "Object Name",
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "defaultValue",
      headerName:
        t("nodeComponents.configuration.defaultValue") || "Default Value",
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "nodeValue",
      headerName: t("nodeComponents.configuration.value") || "Value",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const { row } = params;
        const matchedObject = objectData.find(
          (obj) => obj.idObject === row.object_idObject
        );

        let isInvalid = false;

        if (
          matchedObject &&
          row.nodeValue !== null &&
          row.nodeValue !== undefined
        ) {
          if (matchedObject.type === 1) {
            if (matchedObject.enumList && matchedObject.enumList.length > 0) {
              const enumIdList = matchedObject.enumList
                .split(",")
                .map((id) => Number(id.trim()));
              const filteredEnumData = enumData.filter((enumItem) =>
                enumIdList.includes(enumItem.idEnum)
              );

              const currentLang = (localStorage.getItem("i18nextLng") || "tr").split("-")[0];
              const validValues = filteredEnumData.map((enumItem) => {
                if (currentLang === "tr") {
                  return enumItem.name1;
                } else if (currentLang === "de") {
                  return enumItem.name3;
                } else {
                  return enumItem.name2;
                }
              });

              isInvalid = !validValues.includes(row.nodeValue);
            }
          } else if (matchedObject.type === 0) {
            const booleanValues = ["true", "false", "1", "0", "True", "False"];
            isInvalid = !booleanValues.includes(String(row.nodeValue));
          } else if (
            matchedObject.type === 2 ||
            matchedObject.type === 10 ||
            matchedObject.type === 11 ||
            matchedObject.type === 12
          ) {
            isInvalid = isNaN(Number(row.nodeValue));
          } else if (matchedObject.type === 5) {
            isInvalid = isNaN(parseFloat(row.nodeValue));
          }
        }

        return (
          <div
            style={{
              backgroundColor: isInvalid
                ? theme.palette.mode === "dark"
                  ? "#4a1a1a"
                  : "#ff7f7f"
                : "transparent",
              color: isInvalid
                ? theme.palette.mode === "dark"
                  ? "#ffffff"
                  : "#000000"
                : "inherit",
              padding: "4px 8px",
              borderRadius: "4px",
              width: "100%",
              textAlign: "left",
            }}
          >
            {row.nodeValue}
          </div>
        );
      },
    },

    {
      field: "operations",
      headerName: t("nodeComponents.configuration.operations"),
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const hasValue =
          params.row.nodeValue !== null && params.row.nodeValue !== undefined;
        const rowId =
          params.row.idNodeConfiguration ||
          `${params.row.object_idObject}-${params.row.deviceType}-${
            params.row.node_idNode || "default"
          }`;

        return (
          <IconButton
            onClick={(event) => handleMenuClick(event, rowId)}
            size="small"
          >
            {hasValue ? <MoreHorizIcon /> : <FileUploadIcon />}
          </IconButton>
        );
      },
    },
  ];

  const refreshConfigurations = useCallback(async () => {
    try {
      if (node_id !== null) {
        const result = await getNodeConfigurationsByNodeId(node_id);

        const processedData = result.map((item) => {
          const matchedObject = objectData.find(
            (obj) => obj.idObject === item.object_idObject
          );

          const objectName = matchedObject
            ? matchedObject.name
            : `Unknown (${item.object_idObject})`;

          let convertedNodeValue = item.value;
          let convertedDefaultValue = item.defaultValue;

          if (matchedObject) {
            try {
              if (item.value !== null && item.value !== undefined) {
                convertedNodeValue = ConvertOIDValueToString(
                  matchedObject,
                  item.value
                );
              }
            } catch (error) {
              convertedNodeValue = item.value;
            }

            try {
              if (
                item.defaultValue !== null &&
                item.defaultValue !== undefined
              ) {
                convertedDefaultValue = ConvertOIDValueToString(
                  matchedObject,
                  item.defaultValue
                );
              }
            } catch (error) {
              convertedDefaultValue = item.defaultValue;
            }
          }

          return {
            ...item,
            objectName,
            nodeValue: convertedNodeValue,
            defaultValue: convertedDefaultValue,
          };
        });

        setAllRows(processedData || []);
        setRows(processedData || []);
      }
    } catch (error) {
      console.error("Error fetching node configurations:", error);
    }
  }, [node_id]);

  useEffect(() => {
    refreshConfigurations();
  }, [node_id, refreshConfigurations]);

  useEffect(() => {
    async function fetchDevices() {
      try {
        const response = await getDevices();

        const devicesData = Array.isArray(response) ? response : response.data;

        if (devicesData && Array.isArray(devicesData)) {
          const filtered = devicesData.filter(
            (device) => device.node_idNode === node_id
          );
          setDevices(filtered);

          if (filtered.length === 1) {
            setSelectedDevice(filtered[0].idDevice);
          }
        } else {
          setDevices([]);
        }
      } catch (error) {
        console.error("Error fetching devices:", error);
        setDevices([]);
      }
    }
    if (node_id !== null) fetchDevices();
  }, [node_id]);

  useEffect(() => {
    if (selectedDevice === 0) {
      setRows(allRows);
    } else {
      const selectedDeviceData = devices.find(
        (device) => device.idDevice === selectedDevice
      );
      if (selectedDeviceData) {
        const filteredRows = allRows.filter(
          (row) => row.deviceType === selectedDeviceData.type
        );
        setRows(filteredRows);
      }
    }
  }, [selectedDevice, allRows, devices]);

  // RCU: configurations are grouped into tabs by uiGroupId. The tab label is the
  // group's uiGroupName for the active language (tr=1, en=2, de=3, fr=4, es=5),
  // matching the lettaLanguageType used when fetching Objects at login.
  const isRcu = getCurrentTenant().projectCode === "RCU";
  const languageType = (() => {
    const lang = (i18n.language || "tr").toLowerCase();
    if (lang.startsWith("tr")) return 1;
    if (lang.startsWith("de")) return 3;
    if (lang.startsWith("fr")) return 4;
    if (lang.startsWith("es")) return 5;
    return 2;
  })();

  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const groups = useMemo(() => {
    if (!isRcu) return [];
    const map = new Map();
    rows.forEach((r) => {
      const gid = r.uiGroupId ?? 0;
      if (!map.has(gid)) {
        const label =
          r[`uiGroupName${languageType}`] ||
          r.uiGroupName2 ||
          r.uiGroupName1 ||
          `Group ${gid}`;
        map.set(gid, { id: gid, label });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.id - b.id);
  }, [rows, isRcu, languageType]);

  // Keep the selected tab valid as the available groups change.
  useEffect(() => {
    if (!isRcu) return;
    if (groups.length === 0) {
      if (selectedGroupId !== null) setSelectedGroupId(null);
      return;
    }
    if (!groups.some((g) => g.id === selectedGroupId)) {
      setSelectedGroupId(groups[0].id);
    }
  }, [groups, isRcu, selectedGroupId]);

  const displayRows =
    isRcu && selectedGroupId !== null
      ? rows.filter((r) => (r.uiGroupId ?? 0) === selectedGroupId)
      : rows;

  return (
    <>
      <Box sx={{ maxWidth: { sm: "100%", md: "1700px" } }}>
        <Card
          sx={{
            height: "100%",
            mt: 2,
            mb: 2,
            borderWidth: 2,
            borderStyle: "solid",
          }}
        >
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel id="global-device-select" shrink={true}>
                  {t("nodeComponents.configuration.deviceSerial")}
                </InputLabel>
                <Select
                  labelId="global-device-select"
                  value={selectedDevice}
                  onChange={(e) => setSelectedDevice(e.target.value)}
                  label={t("nodeComponents.configuration.deviceSerial")}
                >
                  <MenuItem key="all" value={0}>
                    {t("nodeComponents.configuration.allDevices") ||
                      "All Devices"}
                  </MenuItem>
                  {devices.map((device) => (
                    <MenuItem key={device.serialDevice} value={device.idDevice}>
                      {device.serialDevice}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box flex={1} />
              <Typography variant="h3" fontWeight="bold">
                {t("nodeComponents.configuration.title")}
              </Typography>
              <Box flex={1} />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendConfigurations}
                sx={{ height: 45, width: 200 }}
                disabled={isLoadingSendConfig}
              >
                {isLoadingSendConfig ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CircularProgress color="inherit" size={18} thickness={5} />
                    <span>
                      {t("nodeComponents.command.loadingResponse") ||
                        "Sending..."}
                    </span>
                  </Stack>
                ) : (
                  t("nodeComponents.configuration.sendConfigurations") ||
                  "Send Configurations"
                )}
              </Button>
              <Snackbar
                open={!!cmdResponseSendConfig}
                autoHideDuration={6000}
                onClose={() => setCmdResponseSendConfig(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              >
                <Alert
                  onClose={() => setCmdResponseSendConfig(null)}
                  severity={
                    cmdResponseSendConfig &&
                    cmdResponseSendConfig.startsWith("Error")
                      ? "error"
                      : "success"
                  }
                  sx={{ width: "100%" }}
                  variant="filled"
                  elevation={6}
                >
                  {cmdResponseSendConfig}
                </Alert>
              </Snackbar>
            </Stack>
          </CardContent>
        </Card>
      </Box>
      {isRcu && groups.length > 0 && (
        <Box sx={{ maxWidth: { sm: "100%", md: "1700px" }, mb: 2 }}>
          <Tabs
            value={selectedGroupId ?? false}
            onChange={(e, v) => setSelectedGroupId(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 0,
              p: 0.5,
              borderRadius: 2,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.035)",
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTabs-flexContainer": { gap: 0.5 },
              "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
            }}
          >
            {groups.map((g) => (
              <Tab
                key={g.id}
                label={g.label}
                value={g.id}
                disableRipple
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  minHeight: 36,
                  minWidth: "auto",
                  px: 2,
                  py: 0.75,
                  borderRadius: 1.5,
                  color: "text.secondary",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    color: "text.primary",
                    bgcolor: (theme) => theme.palette.action.hover,
                  },
                  "&.Mui-selected": {
                    color: (theme) => theme.palette.background.paper,
                    bgcolor: "text.primary",
                    boxShadow: 2,
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>
      )}
      <DataGrid
        columns={columns}
        rows={displayRows}
        getRowId={(row) =>
          row.idNodeConfiguration
            ? row.idNodeConfiguration
            : `${row.object_idObject}-${row.deviceType}-${
                row.node_idNode || "default"
              }`
        }
        pagination
        initialState={{
          pagination: {
            paginationModel: { pageSize: 20 },
          },
        }}
        pageSizeOptions={[20]}
        disableSelectionOnClick
        sx={{
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
          },
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {(() => {
          const selectedRow = allRows.find(
            (row) =>
              row.idNodeConfiguration === selectedRowId ||
              `${row.object_idObject}-${row.deviceType}-${
                row.node_idNode || "default"
              }` === selectedRowId
          );

          const hasValue =
            selectedRow &&
            selectedRow.nodeValue !== null &&
            selectedRow.nodeValue !== undefined;

          if (hasValue) {
            return [
              <MenuItemAction onClick={handleUpdate} key="update">
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t("nodeComponents.configuration.update")}
                />
              </MenuItemAction>,
              <MenuItemAction onClick={handleDelete} key="delete">
                <ListItemIcon>
                  <RestartAltIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t("nodeComponents.configuration.delete")}
                />
              </MenuItemAction>,
            ];
          } else {
            return (
              <MenuItemAction onClick={handleAddConfigurationToNode}>
                <ListItemIcon>
                  <FileUploadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    t("nodeComponents.configuration.addConfigurationToNode") ||
                    "Add Configuration to Node"
                  }
                />
              </MenuItemAction>
            );
          }
        })()}
      </Menu>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, textAlign: "center" }}>
          {editingConfig
            ? t("nodeComponents.configuration.updateConfiguration") ||
              "Update Configuration"
            : t("nodeComponents.configuration.addConfiguration") ||
              "Add Configuration"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth disabled>
              <InputLabel id="object-label" shrink>
                {t("nodeComponents.configuration.object") || "Object"}
              </InputLabel>
              <Select
                labelId="object-label"
                value={formData.object_idObject}
                label={t("nodeComponents.configuration.object") || "Object"}
                onChange={(e) =>
                  handleFormChange("object_idObject", e.target.value)
                }
                displayEmpty
                notched
                disabled
              >
                {(() => {
                  const configObjects = objectData.filter(
                    (obj) => obj.isConfig === 1
                  );
                  return configObjects.map((obj) => (
                    <MenuItem key={obj.idObject} value={obj.idObject}>
                      {obj.name}
                    </MenuItem>
                  ));
                })()}
              </Select>
            </FormControl>

            {(() => {
              const selectedObject = objectData.find(
                (obj) => obj.idObject === parseInt(formData.object_idObject, 10)
              );

              if (selectedObject && selectedObject.type === 1) {
                const currentLang = (localStorage.getItem("i18nextLng") || "tr").split("-")[0];

                let filteredEnumData = enumData;
                let enumIdList = [];
                if (
                  selectedObject.enumList &&
                  selectedObject.enumList.length > 0
                ) {
                  enumIdList = selectedObject.enumList
                    .split(",")
                    .map((id) => Number(id.trim()));
                  filteredEnumData = enumData.filter((enumItem) =>
                    enumIdList.includes(enumItem.idEnum)
                  );
                }

                return (
                  <FormControl fullWidth>
                    <InputLabel id="enum-value-label">
                      {t("nodeComponents.configuration.value") || "Value"}
                    </InputLabel>
                    <Select
                      labelId="enum-value-label"
                      value={formData.value}
                      label={t("nodeComponents.configuration.value") || "Value"}
                      onChange={(e) =>
                        handleFormChange("value", e.target.value)
                      }
                    >
                      {filteredEnumData.map((enumItem) => {
                        let displayName = "";
                        if (currentLang === "tr") {
                          displayName = enumItem.name1;
                        } else if (currentLang === "de") {
                          displayName = enumItem.name3;
                        } else {
                          displayName = enumItem.name2;
                        }
                        return (
                          <MenuItem key={enumItem.idEnum} value={displayName}>
                            {displayName}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                );
              }

              return (
                <TextField
                  fullWidth
                  label={t("nodeComponents.configuration.value") || "Value"}
                  value={formData.value}
                  onChange={(e) => handleFormChange("value", e.target.value)}
                  variant="outlined"
                  // Allow editing value even during update
                  disabled={false}
                  helperText={(() => {
                    if (selectedObject) {
                      switch (selectedObject.type) {
                        case 0:
                          return t(
                            "nodeComponents.configuration.valueTypes.boolean"
                          );
                        case 2:
                          return t(
                            "nodeComponents.configuration.valueTypes.integer"
                          );
                        case 5:
                          return t(
                            "nodeComponents.configuration.valueTypes.decimal"
                          );
                        case 10:
                          return t(
                            "nodeComponents.configuration.valueTypes.multiplied1000"
                          );
                        case 11:
                          return t(
                            "nodeComponents.configuration.valueTypes.multiplied100"
                          );
                        case 12:
                          return t(
                            "nodeComponents.configuration.valueTypes.multiplied10"
                          );
                        default:
                          return t(
                            "nodeComponents.configuration.valueTypes.text"
                          );
                      }
                    }
                    return "";
                  })()}
                />
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDialogClose}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={(() => {
              const hasValue =
                formData.value !== null &&
                formData.value !== undefined &&
                String(formData.value).length > 0;
              if (!editingConfig) {
                return (
                  isSubmittingConfig ||
                  !formData.deviceType ||
                  !formData.object_idObject ||
                  !hasValue
                );
              }
              return isSubmittingConfig || !hasValue;
            })()}
            sx={{ borderRadius: 2 }}
          >
            {isSubmittingConfig ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress color="inherit" size={18} thickness={5} />
                <span>
                  {t("common.saving") ||
                    (editingConfig ? "Updating..." : "Adding...")}
                </span>
              </Stack>
            ) : editingConfig ? (
              t("common.update") || "Update"
            ) : (
              t("common.add") || "Add"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, textAlign: "center" }}>
          {t("nodeComponents.configuration.deleteConfirmTitle") ||
            "Delete Configuration"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, textAlign: "center" }}>
          <Typography>
            {t("nodeComponents.configuration.deleteConfirmMessage") ||
              "Are you sure you want to delete this configuration?"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "center" }}>
          <Button
            onClick={handleDeleteCancel}
            color="primary"
            variant="outlined"
            disabled={isDeleting}
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            sx={{ borderRadius: 2 }}
          >
            {isDeleting ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress color="inherit" size={18} thickness={5} />
                <span>{t("common.deleting") || "Deleting..."}</span>
              </Stack>
            ) : (
              t("nodeComponents.configuration.delete") || "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
